import React from 'react';

import { AiChat } from '~/components/AiChat/containers/AiChat';
import { ClaimActionButtonContainer } from '~/components/ClaimPage';
import { claimPageBarClass } from '~/components/ClaimPage/ClaimPageBar/ClaimPageBarUtils';
import useClaimActiveTab from '~/components/ClaimPage/hooks/useClaimActiveTab';
import CloseClaimContainer from '~/components/CloseClaimContainer';
import useOrganization from '~/components/OrganizationContext';
import cn from '~/Utils/cn';

import useClaimFieldsConfiguration from '../hooks/useClaimFieldsConfiguration';
import useFetchResolvedClaimBar, { type ResolvedClaimBarEntry } from '../hooks/useFetchResolvedClaimBar';
import { CLAIM_FIELD_CONFIG_TYPE } from '../types';

import Broker from './claimBarFieldComponents/Broker';
import ClaimAlertsWrapper from './claimBarFieldComponents/ClaimAlertsWrapper';
import ClaimComplexity from './claimBarFieldComponents/ClaimComplexity';
import ClaimFlags from './claimBarFieldComponents/ClaimFlags';
import ClaimNumber from './claimBarFieldComponents/ClaimNumber';
import ClaimStage from './claimBarFieldComponents/ClaimStage';
import ConductorHandling from './claimBarFieldComponents/ConductorHandling';
import ConfiguredClaimBarField from './claimBarFieldComponents/ConfiguredClaimBarField';
import DateOfReport from './claimBarFieldComponents/DateOfReport';
import DaysOpen from './claimBarFieldComponents/DaysOpen';
import FileOwner from './claimBarFieldComponents/FileOwner';
import IncidentType from './claimBarFieldComponents/IncidentType';
import Policyholder from './claimBarFieldComponents/Policyholder';
import PolicyNumber from './claimBarFieldComponents/PolicyNumber';
import PreferredContact from './claimBarFieldComponents/PreferredContact';
import Reporter from './claimBarFieldComponents/Reporter';
import SkeletonClaimBarField from './claimBarFieldComponents/SkeletonClaimBarField';
import SubOrganization from './claimBarFieldComponents/SubOrganization';
import TimeOfLoss from './claimBarFieldComponents/TimeOfLoss';
import { ClaimNumberSkeletonValue } from './ClaimNumberContainer';
import { isClaimBarFieldAvailableForOrganization } from './utils';

type ClaimBarFieldComponent = React.ComponentType<{ displayName: string }>;

/**
 * `satisfies` rather than a `Record<...>` annotation, so the fixed keys keep their exact
 * identity in the type of this object instead of being flattened into `string`.
 */
export const claimBarFixedFieldComponents = {
  claim_number: ClaimNumber,
  policy_number: PolicyNumber,
  sub_organization: SubOrganization,
  policyholder: Policyholder,
  preferred_contact: PreferredContact,
  time_of_loss: TimeOfLoss,
  date_of_report: DateOfReport,
  file_owner: FileOwner,
  days_open: DaysOpen,
  incident_type: IncidentType,
  reporter: Reporter,
  claim_alerts: ClaimAlertsWrapper,
  claim_stage: ClaimStage,
  claim_flags: ClaimFlags,
  claim_complexity: ClaimComplexity,
  broker: Broker,
  conductor_handling: ConductorHandling,
} satisfies Record<string, ClaimBarFieldComponent>;

/**
 * The reading view of the same object. A configured key can also be a catalog path
 * (`claim.api.*`), which has no component here, so the lookup has to be allowed to miss - and the
 * miss has to reach the type as `undefined`, which is what keeps the guard below alive: a catalog
 * entry of the Default, shown when the per-claim payload fails, carries no `source` and reaches
 * this lookup.
 */
const fieldKeyToComponentMap: Record<string, ClaimBarFieldComponent | undefined> = claimBarFixedFieldComponents;

// The waiting strip's value row is as tall as its tallest placeholder, so the cell whose value is taller
// than one line of text waits on a placeholder built from its own markup.
const skeletonValueByFieldKey: Partial<Record<keyof typeof claimBarFixedFieldComponents, React.ReactNode>> = {
  claim_number: <ClaimNumberSkeletonValue />,
};
const fieldKeyToSkeletonValue: Record<string, React.ReactNode | undefined> = skeletonValueByFieldKey;

const ClaimPageBarWithConfigurations = () => {
  const { fieldsConfiguration } = useClaimFieldsConfiguration(CLAIM_FIELD_CONFIG_TYPE.CLAIM_BAR);
  // Until this claim's own bar arrives the strip shows a skeleton cell for each of the Default's cells, never
  // the Default itself: for a claim whose LOB or sub-organization has its own configuration the Default is a
  // different bar, and showing one then the other is a visible swap. The Default is shown only if the
  // request fails.
  const { resolvedClaimBar, isWaitingForClaimBar } = useFetchResolvedClaimBar();
  const claimBarEntries: ResolvedClaimBarEntry[] = resolvedClaimBar ?? fieldsConfiguration;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error TS233  useOrganization() not properly typed yet
  const { isClaimSearchEnabled } = useOrganization();
  const { shouldDisplayEmbeddedChat } = useClaimActiveTab();

  return (
    <div className={cn(claimPageBarClass, 'flex flex-row items-center justify-between')}>
      <div
        aria-hidden={isWaitingForClaimBar || undefined}
        // React 18 does not know the attribute, so it is passed as a string; an empty one sets it. React 19
        // types it as a boolean and drops '' as false, so on that upgrade this becomes inert={isWaitingForClaimBar}.
        {...(isWaitingForClaimBar ? { inert: '' } : {})}
        className="grid min-w-0 auto-cols-max grid-flow-col grid-rows-[auto_1fr] gap-y-4 overflow-auto bg-slate-100 text-sm"
      >
        {claimBarEntries.map((entry) => {
          const { field_key, is_enabled, display_name, source } = entry;
          const availableForOrg = isClaimBarFieldAvailableForOrganization(field_key, isClaimSearchEnabled);
          if (!is_enabled || !availableForOrg) return null;
          if (isWaitingForClaimBar) {
            return <SkeletonClaimBarField key={field_key} value={fieldKeyToSkeletonValue[field_key]} />;
          }
          if (source) return <ConfiguredClaimBarField key={field_key} entry={entry} />;

          const FieldComponent = fieldKeyToComponentMap[field_key];
          if (!FieldComponent) return null;

          return <FieldComponent key={field_key} displayName={display_name} />;
        })}
      </div>

      <div className="flex shrink-0 flex-row items-center justify-end gap-20 pl-24">
        <div>
          <ClaimActionButtonContainer />
        </div>
        <div>
          <CloseClaimContainer />
        </div>
      </div>

      {shouldDisplayEmbeddedChat ? <AiChat /> : null}
    </div>
  );
};

export default ClaimPageBarWithConfigurations;
